// PostHog add_to_cart, sent once per successful /cart/add response (never on submit or on an error response).
// The store super property comes from naomi-posthog.js; the gate matches naomi-save.js (nothing is sent or
// queued before consent). props() is pure: tests/naomi-add-to-cart.test.mjs.
window.NaomiAddToCart = {
  // The submit button or the form's place on the page says which surface added the item. The sticky bar and the
  // mobile peek submit the PDP form from outside it, so they are told apart by the submitter (as pdp-bar.js does).
  surface(form, submitter) {
    if (submitter?.closest('[data-pdp-bar-cta]')) return submitter.classList.contains('pdp-peek__btn') ? 'peek' : 'bar';
    if (form.closest('[data-naomi-saved-page]')) return 'saved';
    if (form.closest('naomi-campaign-viewer')) return 'campaign';
    if (form.closest('quick-add-modal, .card-wrapper')) return 'card_quick_add';
    if (form.closest('.featured-product')) return 'featured_product';
    if (form.closest('[id^="MainProduct-"]')) return 'buybox';
    return 'other';
  },
  // line = the /cart/add.js response (prices in cents, presentment currency). quantity is what this add put in the
  // cart; the line may already hold more, so value is the discounted unit price times that quantity.
  props(line, quantity, surface, currency) {
    return {
      product_id: line.product_id,
      variant_id: line.variant_id || line.id,
      product_handle: line.handle,
      product_title: line.product_title,
      variant_title: line.variant_title,
      quantity,
      price: line.price / 100,
      value: Math.round((line.final_line_price / (line.quantity || 1)) * quantity) / 100,
      currency,
      surface,
      // the free ring sizer variant ("Sizer" / "사이즈 확인 후 제작"), same test as pdp-ring-size.liquid
      sizer: /sizer|사이즈 확인/i.test(line.variant_title || ''),
    };
  },
  // Always sendBeacon: the page may navigate to /cart, or an in-app browser may close, before a fetch lands.
  track(line, quantity, form, submitter) {
    try {
      if (!window.NaomiPostHog?.ready) return;
      const surface = this.surface(form, submitter);
      const props = this.props(line, quantity, surface, window.Shopify?.currency?.active);
      window.posthog?.capture('add_to_cart', props, { transport: 'sendBeacon' });
    } catch (e) {
      console.error(e);
    }
  },
};

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        // A second tap while the first add is in flight: bring the loading drawer back, add nothing.
        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          if (this.optimistic) this.cart.showLoading(this.submitButton);
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        // Cart drawer: open it now in a loading state instead of spinning the button
        // for the ~1s /cart/add round trip (plus the BOGOS app's /cart.js refetch).
        this.optimistic = !!this.cart?.showLoading && !this.closest('quick-add-modal');
        if (this.optimistic) {
          // the button pressed, not document.activeElement: Safari leaves focus on body after a click
          this.cart.showLoading(evt.submitter || this.submitButton);
        } else {
          this.submitButton.classList.add('loading');
          this.querySelector('.loading__spinner').classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (!this.optimistic) this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (!response.status) window.NaomiAddToCart.track(response, Number(formData.get('quantity')) || 1, this.form, evt.submitter);
            if (response.status) {
              if (this.optimistic) this.cart.hideLoading();
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
            this.cart.classList.remove('is-empty');

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            if (this.optimistic) this.cart.hideLoading();
          })
          .finally(() => {
            this.optimistic = false;
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
